import React, { useRef } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { SkeletonBox } from '@eduact/ed-components'
import { Icon as AbwIcon } from './Icon'

export type FeaturedRailProps = {
	/** Section heading (already localized by the caller). */
	title: string
	/** "see all" action; omitted → no see-all button. Label defaults to عرض الكل. */
	onSeeAll?: () => void
	seeAllLabel?: string
	/** While true the rail shows skeleton cards instead of children. */
	loading?: boolean
	skeletonCount?: number
	/** The card elements to scroll through. */
	children?: React.ReactNode
}

/**
 * The horizontal "featured" rail shared by every landing carousel (courses,
 * bundles, …): heading + desktop scroll arrows + "see all", over a snap-scroll
 * track. Self-hiding/empty logic and the data query stay with each caller; this
 * owns only the chrome and the RTL-aware scroll.
 */
export const FeaturedRail: React.FC<FeaturedRailProps> = ({ title, onSeeAll, seeAllLabel, loading, skeletonCount = 4, children }) => {
	const [, i18n] = useTranslation()
	const isRtl = i18n.dir() === 'rtl'
	const railRef = useRef<HTMLDivElement>(null)

	const scrollRail = (dir: 'prev' | 'next') => {
		const el = railRef.current
		if (!el) return
		const amount = el.clientWidth * 0.8
		const sign = dir === 'prev' ? (isRtl ? 1 : -1) : isRtl ? -1 : 1
		el.scrollBy({ left: sign * amount, behavior: 'smooth' })
	}

	return (
		<Section>
			<HeadRow>
				<Title>{title}</Title>
				<HeadControls>
					<ArrowGroup>
						<RoundArrow type="button" aria-label="prev" onClick={() => scrollRail('prev')}>
							<span style={{ transform: isRtl ? 'scaleX(-1)' : 'none', display: 'flex' }}>
								<AbwIcon name="chevron-left" size={15} />
							</span>
						</RoundArrow>
						<RoundArrow type="button" aria-label="next" onClick={() => scrollRail('next')}>
							<span style={{ transform: isRtl ? 'none' : 'scaleX(-1)', display: 'flex' }}>
								<AbwIcon name="chevron-left" size={15} />
							</span>
						</RoundArrow>
					</ArrowGroup>
					{onSeeAll && (
						<SeeAll type="button" onClick={onSeeAll}>
							{seeAllLabel ?? 'عرض الكل'}
							<Chevron>
								<AbwIcon name="chevron-left" size={14} />
							</Chevron>
						</SeeAll>
					)}
				</HeadControls>
			</HeadRow>
			<Rail ref={railRef}>
				{loading ? Array.from({ length: skeletonCount }).map((_, i) => <SkeletonBox key={i} isLoading width="100%" height="230px" />) : children}
			</Rail>
		</Section>
	)
}

export default FeaturedRail

const Section = styled.section`
	display: flex;
	flex-direction: column;
	gap: 12px;
`
const HeadRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`
const Title = styled.h2`
	margin: 0;
	font: var(--fw-bold) 16px var(--font-sans);
	color: var(--text-primary);
	text-align: start;
`
const HeadControls = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex: none;
`
const ArrowGroup = styled.div`
	display: none;
	@media (min-width: 1024px) {
		display: flex;
		gap: 4px;
	}
`
const RoundArrow = styled.button`
	width: 28px;
	height: 28px;
	border: 1px solid var(--border);
	border-radius: var(--r-full);
	background: var(--surface-card);
	color: var(--text-secondary);
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: box-shadow var(--dur-fast) var(--ease);
	&:hover {
		box-shadow: var(--sh-hover);
	}
`
const SeeAll = styled.button`
	border: none;
	background: none;
	padding: 0;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	gap: 2px;
	font: var(--fw-bold) 13px var(--font-sans);
	font-family: var(--font-sans);
	color: var(--blue);
`
const Chevron = styled.span`
	display: flex;
	html:not([dir='rtl']) & {
		transform: scaleX(-1);
	}
`
/* 2 cards per mobile viewport → constant 208px cards on desktop. */
const Rail = styled.div`
	display: flex;
	gap: 8px;
	overflow-x: auto;
	padding-bottom: 6px;
	scroll-snap-type: x proximity;
	scrollbar-width: none;
	-ms-overflow-style: none;
	&::-webkit-scrollbar {
		display: none;
	}
	> * {
		flex: 0 0 calc((100vw - 40px) / 2);
		max-width: 236px;
		scroll-snap-align: start;
	}
	@media (min-width: 1024px) {
		> * {
			flex: 0 0 208px;
			max-width: none;
		}
	}
`
